/**
 * AdminStatsPage — Cross-organizatie statistieken dashboard (FASE 5).
 *
 * Toont: omzet, MRR, users, trials, codes, commissies, website-aanvragen, top partners.
 * Export-knoppen: CSV commissies, CSV facturen, SEPA pain.001 XML batch.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  TrendingUp, RefreshCw, Loader2, Download, FileText, Coins, Users,
  Ticket, Tag, Globe, BarChart3, X, Save,
} from 'lucide-react';

const formatEUR = (n) => `€${(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminStatsPage() {
  const { api, getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSepaModal, setShowSepaModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/admin/stats/overview');
      setData(r.data);
    } catch {
      toast.error('Statistieken laden mislukt');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async (url, filename) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const token = getToken();
      const resp = await fetch(`${backend}${url}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Download faalde');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      toast.success('Download gestart');
    } catch {
      toast.error('Download mislukt');
    }
  };

  if (loading || !data) return <div className="text-white/40 min-h-screen bg-[#0f172a] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Statistieken & Export</h1>
            <p className="text-white/60 text-sm mt-1">Cross-rol overzicht + downloads voor financiële verwerking</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => downloadCsv('/api/admin/exports/commissions.csv', 'commissies.csv')} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="export-commissions-csv">
              <Download size={14} /> Commissies CSV
            </button>
            <button onClick={() => downloadCsv('/api/admin/exports/invoices.csv', 'facturen.csv')} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="export-invoices-csv">
              <Download size={14} /> Facturen CSV
            </button>
            <button onClick={() => setShowSepaModal(true)} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="open-sepa-modal" title="Optioneel — Stripe Connect Payouts is de aanbevolen route">
              <FileText size={14} /> SEPA-XML (handmatig)
            </button>
            <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="stats-refresh">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* Omzet + MRR row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Kpi label="Omzet deze maand (incl. btw)" value={formatEUR(data.omzet.huidige_maand_incl_vat)} sub={`${data.omzet.paid_this_month_count} paid orders`} color="text-emerald-400" icon={TrendingUp} testid="kpi-omzet" />
          <Kpi label="MRR (ex. btw)" value={formatEUR(data.mrr.ex_vat)} sub={`${data.mrr.active_subscriptions} actieve subs`} color="text-[#D4A017]" icon={BarChart3} testid="kpi-mrr" />
          <Kpi
            label="Groei t.o.v. vorige maand"
            value={data.omzet.groei_pct !== null ? `${data.omzet.groei_pct >= 0 ? '+' : ''}${data.omzet.groei_pct}%` : '—'}
            sub={`Vorige maand: ${formatEUR(data.omzet.vorige_maand_incl_vat)}`}
            color={data.omzet.groei_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}
            icon={TrendingUp}
            testid="kpi-groei"
          />
        </div>

        {/* Stats overige */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SmallStat label="Users (totaal/paid)" value={`${data.users.total} / ${data.users.paid}`} icon={Users} testid="stat-users" />
          <SmallStat label="Trials (actief/converted)" value={`${data.trials.actief} / ${data.trials.geconverteerd}`} icon={Ticket} testid="stat-trials" />
          <SmallStat label="Kortingscodes (totaal/gebruikt)" value={`${data.codes.totaal} / ${data.codes.gebruikt}`} icon={Tag} testid="stat-codes" />
          <SmallStat label="Website-aanvragen" value={`${data.website_aanvragen.totaal}`} sub={`${data.website_aanvragen.in_uitvoering} in uitv. · ${data.website_aanvragen.opgeleverd} opgeleverd`} icon={Globe} testid="stat-website-reqs" />
        </div>

        {/* Commissies block */}
        <div className="bg-white/5 rounded-2xl p-5 mb-5 border border-white/10">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-bold flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              <Coins size={16} className="text-[#D4A017]" /> Commissies
            </h2>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-amber-400" data-testid="stat-com-pending">Open: {formatEUR(data.commissies.open_ex_vat)} ({data.commissies.open_count})</span>
              <span className="text-emerald-400" data-testid="stat-com-paid">Betaald: {formatEUR(data.commissies.betaald_ex_vat)} ({data.commissies.betaald_count})</span>
            </div>
          </div>

          {data.commissies.top_partners?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-white/50 uppercase tracking-wide font-bold mb-2">Top ontvangers</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/40 text-xs">
                    <th className="py-2">Ontvanger</th>
                    <th className="py-2">Rol</th>
                    <th className="py-2 text-right">Totaal</th>
                    <th className="py-2 text-right">Open</th>
                    <th className="py-2 text-right">#</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.commissies.top_partners.map((p) => (
                    <tr key={p._id} data-testid={`top-partner-${p._id}`}>
                      <td className="py-2 text-white">{p.recipient_name || p.recipient_email || p._id?.slice(0, 8)}</td>
                      <td className="py-2 text-white/60 text-xs uppercase">{p.recipient_role}</td>
                      <td className="py-2 text-right text-white font-bold">{formatEUR(p.total_ex_vat)}</td>
                      <td className="py-2 text-right text-amber-400">{formatEUR(p.pending)}</td>
                      <td className="py-2 text-right text-white/60">{p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showSepaModal && <SepaBatchModal getToken={getToken} onClose={() => setShowSepaModal(false)} />}
      </div>
    </div>
  );
}


function Kpi({ label, value, sub, color, icon: Icon, testid }) {
  return (
    <div className="bg-white/5 rounded-2xl p-5 border border-white/10" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color} />
        <p className="text-white/50 text-xs uppercase tracking-wide font-bold">{label}</p>
      </div>
      <p className={`text-3xl font-bold ${color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{value}</p>
      {sub && <p className="text-white/40 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function SmallStat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="text-white/40" />
        <p className="text-white/50 text-xs uppercase tracking-wide font-semibold">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>{value}</p>
      {sub && <p className="text-white/40 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}


function SepaBatchModal({ getToken, onClose }) {
  const [debtorIban, setDebtorIban] = useState('');
  const [debtorBic, setDebtorBic] = useState('');
  const [debtorName, setDebtorName] = useState('IMPERIA AI');
  const [executionDate, setExecutionDate] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!debtorIban) {
      toast.error('Vul IBAN in');
      return;
    }
    setGenerating(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${backend}/api/admin/exports/sepa-payouts.xml`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ debtor_iban: debtorIban, debtor_bic: debtorBic, debtor_name: debtorName, execution_date: executionDate }),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => null);
        throw new Error(errBody?.detail || `Status ${resp.status}`);
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      const batchId = resp.headers.get('X-Imperia-Batch-Id') || 'sepa-batch';
      a.href = URL.createObjectURL(blob);
      a.download = `sepa-payouts-${batchId}.xml`;
      a.click();
      toast.success(`Batch gegenereerd: ${resp.headers.get('X-Imperia-Tx-Count')} betalingen, totaal € ${resp.headers.get('X-Imperia-Total-Amount')}`);
      onClose();
    } catch (e) {
      toast.error(`Mislukt: ${e.message}`);
    }
    setGenerating(false);
  };

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="sepa-batch-modal">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>SEPA pain.001 batch genereren</h2>
          <button onClick={onClose}><X size={18} className="text-white/40 hover:text-white" /></button>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 text-xs text-amber-200">
          <strong>Tip:</strong> commissies worden meestal sneller uitbetaald via <em>Stripe Connect Payouts</em> in uw Stripe-dashboard.
          Gebruik deze XML-export alleen als u handmatig wilt uitbetalen via uw zakelijke bank.
        </div>
        <p className="text-white/60 text-xs mb-4">Genereert XML voor bulk-overboeking aan partners (open commissies). Upload het XML-bestand naar uw zakelijke bankportaal.</p>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Uw zakelijke IBAN *</span>
            <input data-testid="sepa-debtor-iban" value={debtorIban} onChange={(e) => setDebtorIban(e.target.value.toUpperCase().replace(/\s/g, ''))} placeholder="NL00 ABNA 0000 0000 00" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">BIC (optioneel)</span>
            <input value={debtorBic} onChange={(e) => setDebtorBic(e.target.value.toUpperCase())} placeholder="ABNANL2A" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Naam debiteur</span>
            <input value={debtorName} onChange={(e) => setDebtorName(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Uitvoeringsdatum</span>
            <input type="date" value={executionDate} onChange={(e) => setExecutionDate(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          </label>
          <button onClick={generate} disabled={generating} data-testid="generate-sepa-batch" className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Genereer & download
          </button>
        </div>
      </div>
    </div>
  );
}
